import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Upload, Loader2, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/customSupabaseClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const FUNCTION_NAME = 'vimeo-video-settings';
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const VIEW_OPTIONS = [
  { value: 'unlisted', label: 'Unlisted (link only)', hint: 'Standard for exercise videos' },
  { value: 'disable', label: 'Hidden from Vimeo (embed only)' },
  { value: 'anybody', label: 'Public on Vimeo' },
];

const VIEW_LABELS = Object.fromEntries(VIEW_OPTIONS.map((option) => [option.value, option.label]));

function formatSeconds(seconds) {
  const total = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(total / 60);
  const rest = (total % 60).toFixed(1).padStart(4, '0');
  return `${minutes}:${rest}`;
}

async function invokeSettings(body) {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, { body });
  if (error) {
    let detail = null;
    try {
      detail = await error.context?.json?.();
    } catch {
      detail = null;
    }
    throw new Error(detail?.error || error.message);
  }
  if (!data?.success) throw new Error(data?.error || 'Vimeo request failed');
  return data;
}

export function VideoSettingsPanel({ exerciseId, playerRef, frames = [], canEditPrivacy, onUpdated }) {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pendingFrame, setPendingFrame] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [privacyDraft, setPrivacyDraft] = useState(null);
  const fileInputRef = useRef(null);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const data = await invokeSettings({ exerciseId, action: 'status' });
      setStatus(data);
      setPrivacyDraft({
        view: data.video.privacy.view,
        download: Boolean(data.video.privacy.download),
      });
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setLoadingStatus(false);
    }
  }, [exerciseId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => () => {
    if (pendingUpload?.previewUrl) URL.revokeObjectURL(pendingUpload.previewUrl);
  }, [pendingUpload]);

  const otherExercises = useMemo(
    () => (status?.exercises || []).filter((exercise) => exercise.id !== exerciseId),
    [status, exerciseId]
  );

  const privacyChanged = Boolean(
    status && privacyDraft && (
      privacyDraft.view !== status.video.privacy.view ||
      privacyDraft.download !== Boolean(status.video.privacy.download)
    )
  );

  const finish = async (data, message) => {
    setStatus(data);
    setPrivacyDraft({ view: data.video.privacy.view, download: Boolean(data.video.privacy.download) });
    toast.success(message, {
      description: data.exercises.length > 1 ? `Updated ${data.exercises.length} exercises that share this video.` : undefined,
    });
    await onUpdated?.();
  };

  const captureCurrentFrame = async () => {
    try {
      const time = await playerRef?.current?.getCurrentTime?.();
      if (time == null) throw new Error('Start the video above, pause on the frame you want, then try again.');
      setPendingUpload(null);
      setPendingFrame({ timeSeconds: Number(time.toFixed(2)) });
    } catch (err) {
      toast.error('Could not read the video position', { description: err.message });
    }
  };

  const applyFrame = async (timeSeconds) => {
    setBusy('frame');
    try {
      const data = await invokeSettings({ exerciseId, action: 'frame', timeSeconds });
      setPendingFrame(null);
      await finish(data, `Thumbnail set from ${formatSeconds(timeSeconds)}`);
    } catch (err) {
      toast.error('Thumbnail not changed', { description: err.message });
    } finally {
      setBusy(null);
    }
  };

  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error('Use a JPEG, PNG or WebP image');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image must be 8 MB or smaller');
      return;
    }
    setPendingFrame(null);
    setPendingUpload({ file, previewUrl: URL.createObjectURL(file) });
  };

  const applyUpload = async () => {
    if (!pendingUpload) return;
    setBusy('upload');
    try {
      const form = new FormData();
      form.append('exerciseId', exerciseId);
      form.append('action', 'upload');
      form.append('file', pendingUpload.file);
      const data = await invokeSettings(form);
      setPendingUpload(null);
      await finish(data, 'Thumbnail uploaded');
    } catch (err) {
      toast.error('Thumbnail not changed', { description: err.message });
    } finally {
      setBusy(null);
    }
  };

  const applyPrivacy = async () => {
    setBusy('privacy');
    try {
      const data = await invokeSettings({ exerciseId, action: 'privacy', ...privacyDraft });
      await finish(data, 'Privacy updated');
    } catch (err) {
      toast.error('Privacy not changed', { description: err.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-base">
          <Camera className="h-4 w-4 mr-2 text-muted-foreground" />
          Video Thumbnail & Privacy
        </CardTitle>
        <CardDescription>
          Changes are made on Vimeo and shown in the app on the next load.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loadingStatus ? (
          <div className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Vimeo settings…
          </div>
        ) : statusError ? (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-red-500">{statusError}</span>
            <Button variant="outline" size="sm" onClick={loadStatus}>Retry</Button>
          </div>
        ) : (
          <>
            {otherExercises.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-600">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                  This video is shared. Changes also apply to: {otherExercises.map((exercise) => exercise.name).join(', ')}.
                </span>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-[200px_1fr]">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Current thumbnail</p>
                <div className="aspect-video overflow-hidden rounded-lg border bg-muted">
                  {status?.video.thumbnailUrl && (
                    <img src={status.video.thumbnailUrl} alt="Current thumbnail" className="h-full w-full object-cover" />
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={captureCurrentFrame} disabled={Boolean(busy)}>
                    <Camera className="h-3.5 w-3.5 mr-1.5" />
                    Use current frame
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={Boolean(busy)}>
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    Upload image
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={IMAGE_TYPES.join(',')}
                    className="hidden"
                    onChange={chooseFile}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Pause the video above on the frame you want, then press "Use current frame".
                </p>

                {pendingFrame && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm">
                    <span>Set the thumbnail to the frame at <strong>{formatSeconds(pendingFrame.timeSeconds)}</strong>?</span>
                    <Button size="sm" onClick={() => applyFrame(pendingFrame.timeSeconds)} disabled={Boolean(busy)}>
                      {busy === 'frame' && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                      Set thumbnail
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPendingFrame(null)} disabled={Boolean(busy)}>
                      Cancel
                    </Button>
                  </div>
                )}

                {pendingUpload && (
                  <div className="flex flex-wrap items-center gap-3 rounded-md border p-3 text-sm">
                    <img src={pendingUpload.previewUrl} alt="New thumbnail preview" className="h-16 aspect-video rounded object-cover" />
                    <Button size="sm" onClick={applyUpload} disabled={Boolean(busy)}>
                      {busy === 'upload' && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                      Set thumbnail
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPendingUpload(null)} disabled={Boolean(busy)}>
                      Cancel
                    </Button>
                  </div>
                )}

                {frames.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Or pick an extracted frame</p>
                    <div className="grid grid-cols-4 gap-2">
                      {frames.map((frame) => (
                        <button
                          key={`${frame.index}-${frame.timestamp_seconds}`}
                          type="button"
                          disabled={Boolean(busy)}
                          onClick={() => {
                            setPendingUpload(null);
                            setPendingFrame({ timeSeconds: frame.timestamp_seconds });
                          }}
                          className="overflow-hidden rounded border hover:ring-2 hover:ring-primary disabled:opacity-50"
                        >
                          <img src={frame.url} alt={`Frame at ${frame.timestamp_seconds}s`} className="aspect-video w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Privacy</p>
                {status?.video.privacy.view && (
                  <Badge variant="secondary" className="text-[10px]">
                    {VIEW_LABELS[status.video.privacy.view] || status.video.privacy.view}
                  </Badge>
                )}
              </div>

              {canEditPrivacy && privacyDraft ? (
                <div className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Who can watch on vimeo.com</Label>
                      <Select
                        value={privacyDraft.view ?? undefined}
                        onValueChange={(view) => setPrivacyDraft((draft) => ({ ...draft, view }))}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {VIEW_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value} className="text-xs">
                              {option.label}{option.hint ? ` · ${option.hint}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end justify-between gap-3 md:justify-start">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="vimeo-download"
                          checked={privacyDraft.download}
                          onCheckedChange={(download) => setPrivacyDraft((draft) => ({ ...draft, download }))}
                        />
                        <Label htmlFor="vimeo-download" className="text-xs">Allow downloads</Label>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Embedding stays allowed everywhere so the app can play the video. Changing to or from
                    Unlisted changes the Vimeo link; it is updated here automatically.
                  </p>
                  <Button size="sm" onClick={applyPrivacy} disabled={!privacyChanged || Boolean(busy)}>
                    {busy === 'privacy' && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    Save privacy
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Downloads {status?.video.privacy.download ? 'allowed' : 'off'} · Embedding {status?.video.privacy.embed === 'public' ? 'allowed everywhere' : status?.video.privacy.embed || 'unknown'}
                  {!canEditPrivacy && ' · Only admins can change privacy.'}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
